import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Collapse,
  Chip,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ModalComponentRecommendations from "./ModalComponentRecommendations.jsx";
import {
  postRecommendation,
  putRecommendation,
} from "../../../services/apiCalls.js";
import ErrorModal from "../../ErrorModal/ErrorModal.jsx";
import { getUserError } from "../../../utils/errors.js";
import SelectedNoteModal from "./SelectedNoteModal.jsx";

const RecommendationsTable = ({
  selectedUser,
  suggestedData,
  feedbackByRecommendationId = {},
  refreshRecommendations,
  loading,
}) => {
  const theme = useTheme();
  // < sm : phones        -> minimal columns
  // sm  : tablets        -> add date + feedback
  // md+ : desktop        -> all columns
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [open, setOpen] = useState(false);
  const [currentRecommendation, setCurrentRecommendation] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [selectedNote, setSelectedNote] = useState("");
  const [openNote, setOpenNote] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [showRejected, setShowRejected] = useState(true);
  const [inputText, setInputText] = useState("");
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [errorOpen, setErrorOpen] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [postRecomLoading, setPostRecomLoading] = useState(false);

  // console.log("Selected user:", selectedUser);
  const { t } = useTranslation("recommendations");
  const { t: tErr } = useTranslation("errors");

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "#dc930c";
      case "Accepted":
        return "#2ccb2f";
      case "Rejected":
        return "#d32f2f";
      case "Modified":
        return "#186480";
      default:
        return "black";
    }
  };

  const transformData = (data, feedbackMap) => {
    const newData = data.map((rec, index) => {
      const fb = feedbackMap?.[rec.RecommendationModel.id] || null;
      return {
        no: index + 1,
        id: rec.RecommendationModel.id,
        recommendation: rec.RecommendationModel.recommendation,
        created_at: format(
          parseISO(rec.RecommendationModel.created_at),
          "dd-MM-yyyy"
        ),
        status: rec.RecommendationModel.status,
        notes: rec.RecommendationModel.notes || "",
        is_duplicate: rec.RecommendationModel.is_duplicate || false,
        feedback_seen: fb ? fb.seen : null,
        feedback_raw: fb ? fb.user_feedback_raw : null,
        has_feedback: Boolean(fb),
        fullRow: rec.RecommendationModel,
      };
    });
    return newData;
  };

  useEffect(() => {
    if (suggestedData && Array.isArray(suggestedData)) {
      const newData = transformData(suggestedData, feedbackByRecommendationId);
      setRecommendations(newData);
    }
  }, [suggestedData, feedbackByRecommendationId]);

  const allStatuses = Array.from(
    new Set(recommendations.map((item) => item.status))
  );

  const columns = [
    {
      field: "no",
      headerName: t("columns.no"),
      align: "center",
      headerAlign: "center",
      flex: 0.3,
      renderCell: (params) => (
        <Typography variant="inherit">{params.value}</Typography>
      ),
    },
    {
      field: "recommendation",
      headerName: t("columns.recommendation"),
      align: "left",
      headerAlign: "center",
      flex: 3,
      renderCell: (params) => {
        // Inline the previously-separate "Type" column as a leading chip on
        // the recommendation text. Keeps the is_duplicate signal visible
        // without spending a full column on one bit of info.
        const isDup = params.row.is_duplicate;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              whiteSpace: "normal",
            }}
          >
            <Tooltip
              title={
                isDup
                  ? t("duplicateLabels.previouslySentTooltip")
                  : t("duplicateLabels.newTooltip")
              }
              arrow
            >
              <Chip
                label={
                  isDup
                    ? t("duplicateLabels.previouslySent")
                    : t("duplicateLabels.new")
                }
                size="small"
                sx={{
                  backgroundColor: isDup ? "#ff9800" : "#4caf50",
                  color: "white",
                  fontWeight: 500,
                  flexShrink: 0,
                }}
              />
            </Tooltip>
            <Typography variant="inherit" sx={{ textAlign: "left" }}>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "created_at",
      headerName: t("columns.dateCreated"),
      align: "center",
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="inherit">{params.value}</Typography>
      ),
    },
    {
      field: "status",
      headerName: t("columns.status"),
      align: "center",
      headerAlign: "center",
      flex: 0.7,
      type: "singleSelect",
      valueOptions: allStatuses,
      renderCell: (params) => (
        <Typography
          variant="inherit"
          sx={{
            color: getStatusColor(params.value),
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "feedback",
      headerName: t("columns.feedback"),
      align: "center",
      headerAlign: "center",
      flex: 1.1,
      sortable: false,
      filterable: false,
      // Render feedback only for ACCEPTED rows — those are the ones NELI
      // forwards to users and can receive a response. Everything else shows
      // a dash so the column reads cleanly at a glance.
      renderCell: (params) => {
        const { status, has_feedback, feedback_seen, feedback_raw } = params.row;
        if (status !== "Accepted") {
          return (
            <Typography variant="inherit" sx={{ color: "#9e9e9e" }}>
              —
            </Typography>
          );
        }
        if (!has_feedback) {
          return (
            <Typography
              variant="inherit"
              sx={{ color: "#9e9e9e", fontStyle: "italic" }}
            >
              {t("feedbackLabels.awaiting")}
            </Typography>
          );
        }
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <Tooltip
              title={
                feedback_seen
                  ? t("feedbackLabels.seenTooltip")
                  : t("feedbackLabels.notSeenTooltip")
              }
              arrow
            >
              {feedback_seen ? (
                <VisibilityIcon sx={{ color: "#2ccb2f", fontSize: 20 }} />
              ) : (
                <VisibilityOutlinedIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
              )}
            </Tooltip>
            {feedback_raw ? (
              <Tooltip title={feedback_raw} arrow>
                <Chip
                  label={feedback_raw}
                  size="small"
                  sx={{
                    backgroundColor: "#186480",
                    color: "white",
                    fontWeight: 500,
                    maxWidth: 120,
                  }}
                />
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: "notes",
      headerName: t("columns.notes"),
      align: "center",
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => {
        const note = params.value;

        return (
          <Typography
            className="notes-cell"
            variant="inherit"
            sx={{
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: note ? "pointer" : "default",
              textAlign: "center",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (note) {
                handleOpenNote(params.row.fullRow);
              }
            }}
          >
            {note}
          </Typography>
        );
      },
    },
  ];

  // Breakpoint-driven column visibility. DataGrid's columnVisibilityModel
  // toggles columns without remounting — no horizontal scroll on phones.
  const columnVisibilityModel = useMemo(() => {
    if (isMobile) {
      return {
        no: true,
        recommendation: true,
        created_at: false,
        status: true,
        feedback: false,
        notes: false,
      };
    }
    if (isTablet) {
      return {
        no: true,
        recommendation: true,
        created_at: true,
        status: true,
        feedback: true,
        notes: false,
      };
    }
    return {
      no: true,
      recommendation: true,
      created_at: true,
      status: true,
      feedback: true,
      notes: true,
    };
  }, [isMobile, isTablet]);

  const handleOpen = (recommendation) => {
    setCurrentRecommendation(recommendation);
    // console.log("set current recommendation: ", recommendation);

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAction = async (action, editedText, reason) => {
    let updatedRecommendation = {
      ...currentRecommendation,
      recommendation: editedText ?? currentRecommendation.recommendation,
    };

    if (action === "reject") {
      updatedRecommendation = {
        ...updatedRecommendation,
        status: "Rejected",
        notes: reason,
      };

      setRecommendations((prev) =>
        prev.map((rec) =>
          rec.id === currentRecommendation.id
            ? { ...rec, status: "Rejected", notes: reason }
            : rec
        )
      );
    } else if (action === "approve") {
      if (currentRecommendation.status === "Accepted") {
        setError(t("errors.acceptedCannotBeModified"));
        return;
      }

      updatedRecommendation = {
        ...updatedRecommendation,
        status: "Accepted",
        notes: reason,
      };

      setRecommendations((prev) =>
        prev.map((rec) =>
          rec.id === currentRecommendation.id
            ? { ...rec, status: "Accepted", notes: reason }
            : rec
        )
      );
    } else if (action === "edit") {
      if (currentRecommendation.status === "Accepted") {
        setError(t("errors.acceptedCannotBeModified"));

        return;
      }
      if (editedText !== currentRecommendation.recommendation) {
        updatedRecommendation = {
          ...updatedRecommendation,
          recommendation: editedText,
          status: "Modified",
          notes: reason,
        };

        setRecommendations((prev) =>
          prev.map((rec) =>
            rec.id === currentRecommendation.id
              ? {
                  ...rec,
                  recommendation: editedText,
                  status: "Modified",
                  notes: reason,
                }
              : rec
          )
        );
      }
    }

    try {
      // console.log("Updated recommendations: ", updatedRecommendation);
      await putRecommendation(updatedRecommendation);
      await refreshRecommendations();
    } catch (error) {
      // console.log("Error when updating recommendation:", error);
      throw error;
    }

    setCurrentRecommendation(updatedRecommendation);
    setOpen(false);
  };

  const handleToggleText = () => {
    if (isAdding) {
      setInputText("");
      setError("");
    }
    setIsAdding(!isAdding);
  };

  const handleChange = (e) => {
    setInputText(e.target.value);
  };

  const handleSave = async () => {
    if (!inputText.trim()) {
      setError(t("errors.enterRecommendation"));
      return;
    }

    const newRecommendation = {
      user_uuid: selectedUser,
      recommendation: inputText,
      status: "Accepted",
    };
    setPostRecomLoading(true);
    try {
      await postRecommendation(newRecommendation);
      await refreshRecommendations();
      setInputText("");
      setIsAdding(false);
    } catch (error) {
      setPostRecomLoading(false);
      // console.log("Error when saving recommendation:", error);
      setErrorTitle(tErr("errorsRecommendation.createTitle"));
      setErrorMsg(getUserError(tErr, "create", error));
      setErrorOpen(true);
    } finally {
      setPostRecomLoading(false);
    }
  };

  const filteredRecommendations = showRejected
    ? recommendations
    : recommendations
        .filter((rec) => rec.status !== "Rejected")
        .map((item, index) => ({
          ...item,
          no: index + 1,
        }));

  // console.log("Filtered recommendations: ", filteredRecommendations);
  const toggleShowRejected = () => {
    setShowRejected(!showRejected);
  };

  const handleOpenNote = (note) => {
    // console.log("Selected Note: ", note);
    setSelectedNote(note);
    setOpenNote(true);
  };
  return (
    <Box
      className="recomTable"
      sx={{
        outline: "none",
        padding: "8px",
        margin: "14px 8px",
        borderRadius: "12px",
      }}
    >
      <DataGrid
        rows={filteredRecommendations}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        filterMode="client"
        loading={loading}
        getRowId={(row) => row.id}
        onRowClick={(params, event) => {
          if (event.target.closest(".notes-cell")) {
            return;
          }

          handleOpen(params.row.fullRow);
        }}
        getRowHeight={() => "auto"}
        sx={{
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-cell": {
            paddingTop: "8px",
            paddingBottom: "8px",
          },
        }}
        pagination
        paginationModel={{
          pageSize: rowsPerPage,
          page: page,
        }}
        onPaginationModelChange={({ page, pageSize }) => {
          setPage(page);
          setRowsPerPage(pageSize);
        }}
        pageSizeOptions={[5, 10, 20, 50]}
      />
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <>
          <Box sx={{ order: 2 }}>
            <Button
              sx={{
                marginTop: "10px",
                gap: "6px",
              }}
              onClick={handleToggleText}
            >
              {isAdding ? (
                <>
                  <CancelIcon
                    sx={{
                      color: "red",
                      "&:hover": {
                        color: "",
                      },
                    }}
                  />
                  {t("buttons.cancel")}
                </>
              ) : (
                <>
                  <AddCircleIcon
                    sx={{
                      color: "orange",
                      "&:hover": {
                        color: "",
                      },
                    }}
                  />
                  {t("buttons.addNew")}
                </>
              )}
            </Button>
          </Box>
          <Box sx={{ order: 1 }}>
            <Button
              sx={{ marginTop: "10px", gap: "6px" }}
              onClick={toggleShowRejected}
            >
              {showRejected ? (
                <>
                  <VisibilityOffIcon
                    sx={{
                      color: "red",
                      "&:hover": {
                        color: "",
                      },
                    }}
                  />
                  {t("buttons.hideRejected")}
                </>
              ) : (
                <>
                  <VisibilityIcon
                    sx={{
                      color: "#00D100",
                      "&:hover": {
                        color: "",
                      },
                    }}
                  />
                  {t("buttons.showRejected")}
                </>
              )}
            </Button>
          </Box>
        </>
      </Box>

      <Collapse in={isAdding}>
        <Box>
          <Typography
            sx={{ marginTop: "20px", marginBottom: "20px" }}
            variant="body1"
          >
            {t("labels.typeRecommendation")}
          </Typography>
          <TextField
            variant="outlined"
            label={t("labels.customRecommendationPlaceholder")}
            multiline
            rows={3}
            sx={{ width: "100%" }}
            value={inputText}
            onChange={handleChange}
            helperText={error}
            FormHelperTextProps={{ style: { color: "red" } }}
          />
          <Box sx={{ textAlign: "right", pt: "5px", marginTop: "5px" }}>
            <LoadingButton
              loading={postRecomLoading}
              loadingPosition="start"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={inputText === ""}
            >
              {t("buttons.save")}
            </LoadingButton>
          </Box>
        </Box>
      </Collapse>
      {selectedNote && (
        <SelectedNoteModal
          selectedRow={selectedNote}
          setOpenNote={setOpenNote}
          openNote={openNote}
          recommendation={currentRecommendation}
        />
      )}

      {currentRecommendation && (
        <ModalComponentRecommendations
          open={open}
          setOpen={setOpen}
          onClose={handleClose}
          recommendation={currentRecommendation}
          onAction={handleAction}
          getStatusColor={getStatusColor}
          refreshRecommendations={refreshRecommendations}
        />
      )}
      <ErrorModal
        open={errorOpen}
        onClose={() => setErrorOpen(false)}
        title={errorTitle}
        message={errorMsg}
      />
    </Box>
  );
};

export default RecommendationsTable;
